import { TemplateFormat } from '@langchain/core/prompts';
import { FormLabel } from '@mui/joy';

import { useCallback, useEffect, useMemo, useRef } from 'react';

import { HubCard } from '@/Pages/HubRepo/HubCard';
import { usePlaygroundStore } from '@/Pages/Playground/PlaygroundHome/hooks/usePlaygroundStore';
import { normalizeKwargsName } from '@/Pages/Playground/utils/Playground.utils';
import { EditableChatInput } from '@/components/EditableChatInput';
import { EditableMessage } from '@/components/EditableMessage';
import ChainIcon from '@/icons/ChainIcon.svg?react';
import {
  MessageCapability,
  MessageUnionType,
  S3DataSchema,
  StoredMessage,
} from '@/types/schema';
import {
  convertOpenAIMessageToStoredMessage,
  isOpenAIMessage,
  isStoredMessage,
} from '@/utils/messages';

import { ManifestPreview } from '../../Manifest';
import {
  KwargsSerializedConstructor,
  ManifestEditorProps,
  ManifestHubProps,
} from '../../Manifest.utils';
import {
  SerializedChatPromptTemplateMessagesKwarg,
  convertManifestToMessages,
  convertMessagesToManifestKwargs,
  extractTemplateFormatFromKwargs,
} from './ManifestChatPromptTemplate.utils';

type SerializedChatPromptTemplate = KwargsSerializedConstructor<{
  messages?: SerializedChatPromptTemplateMessagesKwarg | StoredMessage[];
  input_variables?: string[];
}>;

export function messagesToPatchedManifest(
  manifest: SerializedChatPromptTemplate,
  changeMsgs: MessageUnionType[],
  templateFormat?: TemplateFormat
): SerializedChatPromptTemplate {
  return {
    ...manifest,
    kwargs: {
      ...manifest.kwargs,
      ...convertMessagesToManifestKwargs(changeMsgs, templateFormat),
    },
  };
}

export function ManifestChatPromptTemplate({
  value,
  onChange,
  options,
  withAdditionalKeys,
  hideTemplateFormatSelector,
  actions,
  readOnly,
  disabledMessageTypes,
  disabledMessageCapabilities,
  isAllCollapsed,
  setIsAllCollapsed,
  variableMappingProps,
  onVariableCreated,
}: ManifestEditorProps<SerializedChatPromptTemplate>) {
  const kwargs = value.kwargs;

  const defaultCapabilities = [
    MessageCapability.IMAGE,
    MessageCapability.CANVAS,
  ];

  // Filter out disabled capabilities
  const capabilities = disabledMessageCapabilities
    ? defaultCapabilities.filter(
        (capability) => !disabledMessageCapabilities.includes(capability)
      )
    : defaultCapabilities;

  // check if messages is a StoredMessage - if it is, it's already in message format
  // so we don't need to convert it
  const msgs = kwargs.messages?.every((message) => isOpenAIMessage(message))
    ? kwargs.messages.map((m) => convertOpenAIMessageToStoredMessage(m))
    : kwargs.messages;

  const messageAlreadyFormatted = msgs?.every((message) =>
    isStoredMessage(message)
  );

  const messages = messageAlreadyFormatted
    ? ((kwargs.messages ?? []) as StoredMessage[])
    : convertManifestToMessages(kwargs.messages ?? []);

  const { examples } = usePlaygroundStore();

  const exampleAttachmentUrls = useMemo(() => {
    return examples.reduce((acc, example) => {
      return { ...acc, ...(example.attachment_urls ?? {}) };
    }, {} as Record<string, S3DataSchema>);
  }, [examples]);

  // Older versions of Python lib do not include input_variables in the manifest
  // This patches the manifest to include it
  // TODO: Remove this once in the future
  useEffect(() => {
    if (!('input_variables' in kwargs) || !kwargs.input_variables) {
      onChange(messagesToPatchedManifest(value, messages));
    }
  }, []);

  // use a ref so that setMessages doesn't need to update on every edit
  const valueRef = useRef(value);
  valueRef.current = value;

  const setMessages = useCallback(
    (changeMsgs, newTemplateFormat) => {
      const templateFormat = newTemplateFormat ?? 'f-string';
      onChange(
        messagesToPatchedManifest(valueRef.current, changeMsgs, templateFormat)
      );
    },
    [onChange]
  );

  return (
    <>
      <EditableChatInput
        variant="template"
        capabilities={capabilities}
        messages={messages}
        setMessages={setMessages}
        readOnly={readOnly}
        templateFormat={extractTemplateFormatFromKwargs(kwargs)}
        hideTemplateFormatSelector={hideTemplateFormatSelector}
        actions={actions}
        attachments={exampleAttachmentUrls}
        disabledMessageTypes={disabledMessageTypes}
        isAllCollapsed={isAllCollapsed}
        setIsAllCollapsed={setIsAllCollapsed}
        variableMappingProps={variableMappingProps}
        onVariableCreated={onVariableCreated}
        existingVariables={value.kwargs.input_variables ?? []}
      />

      {withAdditionalKeys &&
        Object.keys(kwargs)
          .filter((key) => key !== 'template')
          .map((key) => {
            return (
              <div key={key} className="flex flex-col gap-2">
                <FormLabel>{normalizeKwargsName(key)}</FormLabel>
                <ManifestPreview value={kwargs[key]} options={options} />
              </div>
            );
          })}
    </>
  );
}

export function ManifestChatPromptTemplateHub(
  props: ManifestHubProps<SerializedChatPromptTemplate>
) {
  const kwargs = props.value.kwargs;
  const messages = convertManifestToMessages(kwargs.messages ?? []);

  return (
    <HubCard
      {...props.cardProps}
      title="ChatPromptTemplate"
      icon={<ChainIcon className="h-4 w-4" />}
    >
      <div className="flex flex-col gap-2">
        {messages.map((message, idx) => {
          return (
            <div key={idx} className="cursor-pointer rounded-lg">
              <EditableMessage
                variant="template"
                messageValue={message}
                onChange={() => null}
                hideSyntaxHighlighting={false}
                templateFormat={
                  extractTemplateFormatFromKwargs(kwargs) ?? 'f-string'
                }
                readOnly
              />
            </div>
          );
        })}
      </div>
    </HubCard>
  );
}
